import type { OptionOrder } from './types';
import { getCloudProgress, saveCloudProgress, type CloudProgress } from './userProgress';

const VERSION = 1;
const SYNC_DEBOUNCE_MS = 2000;

export type QuizStatus = 'idle' | 'playing' | 'paused' | 'finished';

export interface SavedQuizState {
  version: number;
  picks: Record<string, OptionOrder>;
  crossed: Record<string, OptionOrder[]>;
  currentTime: number;
  status: QuizStatus;
  showLiveScript: boolean;
  updatedAt: number;
}

export function quizKey(courseSlug: string, sectionSlug: string): string {
  return `quiz:${courseSlug}:${sectionSlug}`;
}

export function loadQuizState(key: string): SavedQuizState | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedQuizState;
    if (parsed.version !== VERSION) return null;
    if (typeof parsed.currentTime !== 'number') return null;
    if (!parsed.picks || typeof parsed.picks !== 'object') return null;
    if (!parsed.crossed || typeof parsed.crossed !== 'object') parsed.crossed = {};
    parsed.picks = coercePicks(parsed.picks);
    parsed.crossed = coerceCrossed(parsed.crossed);
    return parsed;
  } catch {
    return null;
  }
}

function coercePicks(raw: Record<string, unknown>): Record<string, OptionOrder> {
  const out: Record<string, OptionOrder> = {};
  for (const [qid, v] of Object.entries(raw)) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 1 && n <= 4) out[qid] = n as OptionOrder;
  }
  return out;
}

function coerceCrossed(raw: Record<string, unknown>): Record<string, OptionOrder[]> {
  const out: Record<string, OptionOrder[]> = {};
  for (const [qid, arr] of Object.entries(raw)) {
    if (!Array.isArray(arr)) continue;
    const coerced = arr
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n >= 1 && n <= 4) as OptionOrder[];
    if (coerced.length > 0) out[qid] = coerced;
  }
  return out;
}

function writeLocal(key: string, payload: SavedQuizState): void {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore quota/blocked errors
  }
}

// ---------------------------------------------------------------------------
// Cloud sync — debounced per (uid, courseSlug, sectionSlug)
// ---------------------------------------------------------------------------

interface SyncEntry {
  timer: ReturnType<typeof setTimeout> | null;
  pending: SavedQuizState | null;
  uid: string;
  courseSlug: string;
  sectionSlug: string;
}

const syncRegistry = new Map<string, SyncEntry>();

function syncRegistryKey(uid: string, courseSlug: string, sectionSlug: string): string {
  return `${uid}::${courseSlug}::${sectionSlug}`;
}

function scheduleCloudWrite(
  uid: string,
  courseSlug: string,
  sectionSlug: string,
  state: SavedQuizState,
  immediate = false,
): void {
  const key = syncRegistryKey(uid, courseSlug, sectionSlug);
  let entry = syncRegistry.get(key);
  if (!entry) {
    entry = { timer: null, pending: null, uid, courseSlug, sectionSlug };
    syncRegistry.set(key, entry);
  }
  entry.pending = state;

  const flush = () => {
    const e = syncRegistry.get(key);
    if (!e || !e.pending) return;
    const snapshot = e.pending;
    e.pending = null;
    e.timer = null;
    saveCloudProgress(e.uid, e.courseSlug, e.sectionSlug, {
      picks: snapshot.picks,
      crossed: snapshot.crossed,
      currentTime: snapshot.currentTime,
      status: snapshot.status,
      showLiveScript: snapshot.showLiveScript,
    }).catch(() => {
      // network/permission errors: local copy is the source of truth
    });
  };

  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
  if (immediate) {
    flush();
  } else {
    entry.timer = setTimeout(flush, SYNC_DEBOUNCE_MS);
  }
}

/** Force-flush any pending cloud writes for this section. Useful on unmount/logout. */
export function flushCloudWrite(uid: string, courseSlug: string, sectionSlug: string): void {
  const key = syncRegistryKey(uid, courseSlug, sectionSlug);
  const entry = syncRegistry.get(key);
  if (!entry || !entry.pending) return;
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
  const snapshot = entry.pending;
  entry.pending = null;
  saveCloudProgress(uid, courseSlug, sectionSlug, {
    picks: snapshot.picks,
    crossed: snapshot.crossed,
    currentTime: snapshot.currentTime,
    status: snapshot.status,
    showLiveScript: snapshot.showLiveScript,
  }).catch(() => {});
}

export interface SaveOptions {
  uid?: string | null;
  courseSlug?: string;
  sectionSlug?: string;
  /** Skip cloud sync entirely (used when only currentTime changed during playback). */
  skipCloud?: boolean;
  /** Flush immediately instead of debouncing. Use on status change to 'finished' or on unmount. */
  flushNow?: boolean;
}

export function saveQuizState(
  key: string,
  state: Omit<SavedQuizState, 'version' | 'updatedAt'>,
  options: SaveOptions = {},
): void {
  const payload: SavedQuizState = {
    ...state,
    version: VERSION,
    updatedAt: Date.now(),
  };
  writeLocal(key, payload);

  if (options.skipCloud) return;
  const { uid, courseSlug, sectionSlug, flushNow } = options;
  if (!uid || !courseSlug || !sectionSlug) return;
  scheduleCloudWrite(uid, courseSlug, sectionSlug, payload, flushNow === true);
}

/** Cancel any pending cloud write for this section without firing it. */
export function cancelCloudWrite(uid: string, courseSlug: string, sectionSlug: string): void {
  const key = syncRegistryKey(uid, courseSlug, sectionSlug);
  const entry = syncRegistry.get(key);
  if (!entry) return;
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
  entry.pending = null;
}

export function clearQuizState(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export interface LocalQuizEntry {
  courseSlug: string;
  sectionSlug: string;
  state: SavedQuizState;
}

/** List every locally saved quiz state. Used for migrating guest progress on sign-in. */
export function listLocalQuizStates(): LocalQuizEntry[] {
  const out: LocalQuizEntry[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('quiz:')) continue;
      const rest = key.slice('quiz:'.length);
      const sep = rest.indexOf(':');
      if (sep <= 0) continue;
      const courseSlug = rest.slice(0, sep);
      const sectionSlug = rest.slice(sep + 1);
      const state = loadQuizState(key);
      if (!state) continue;
      out.push({ courseSlug, sectionSlug, state });
    }
  } catch {
    // ignore
  }
  return out;
}

/** Count locally saved quizzes that look "real" (has answers or non-trivial time). */
export function countLocalQuizStates(): number {
  return listLocalQuizStates().filter(
    ({ state }) => Object.keys(state.picks).length > 0 || state.currentTime > 5,
  ).length;
}

/** Remove every locally saved quiz state. Called on sign-out so the next guest starts clean. */
export function clearAllLocalQuizStates(): void {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('quiz:')) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Pull cloud progress and merge into local cache based on updatedAt.
 * Returns the resolved state that should be used by the UI (or null if neither side has data).
 */
export async function hydrateFromCloud(
  uid: string,
  courseSlug: string,
  sectionSlug: string,
): Promise<SavedQuizState | null> {
  const key = quizKey(courseSlug, sectionSlug);
  const local = loadQuizState(key);
  let cloud: CloudProgress | null = null;
  try {
    cloud = await getCloudProgress(uid, courseSlug, sectionSlug);
  } catch {
    return local;
  }
  if (!cloud && !local) return null;
  if (cloud && (!local || cloud.updatedAtMs > local.updatedAt)) {
    const merged: SavedQuizState = {
      version: VERSION,
      picks: coercePicks(cloud.picks),
      crossed: coerceCrossed(cloud.crossed),
      currentTime: cloud.currentTime,
      status: cloud.status,
      showLiveScript: cloud.showLiveScript,
      updatedAt: cloud.updatedAtMs || Date.now(),
    };
    writeLocal(key, merged);
    return merged;
  }
  return local;
}
